import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Home, Users, Award, MapPin } from 'lucide-react';

const TheRiad = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isEn = i18n.language === 'en';

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] }
  };

  const highlights = isEn ? [
    {
      icon: <Home size={32} />,
      title: '400 m²',
      subtitle: 'Of refined spaces',
      desc: 'A spacious residence where every corner breathes Moroccan elegance'
    },
    {
      icon: <Users size={32} />,
      title: '10 guests',
      subtitle: 'Maximum capacity',
      desc: 'Full privatization for demanding families and groups of friends'
    },
    {
      icon: <Award size={32} />,
      title: '5 bedrooms',
      subtitle: 'All unique',
      desc: 'Each with its own color identity and character'
    },
    {
      icon: <MapPin size={32} />,
      title: 'Kasbah',
      subtitle: 'Historic district',
      desc: 'In the heart of the medina, in a quiet and preserved derb'
    }
  ] : [
    {
      icon: <Home size={32} />,
      title: '400 m²',
      subtitle: 'D\'espaces raffinés',
      desc: 'Une demeure spacieuse où chaque recoin respire l\'élégance marocaine'
    },
    {
      icon: <Users size={32} />,
      title: '10 personnes',
      subtitle: 'Capacité maximale',
      desc: 'Privatisation totale pour familles et groupes d\'amis exigeants'
    },
    {
      icon: <Award size={32} />,
      title: '5 chambres',
      subtitle: 'Toutes uniques',
      desc: 'Chacune avec sa propre identité chromatique et son caractère'
    },
    {
      icon: <MapPin size={32} />,
      title: 'Kasbah',
      subtitle: 'Quartier historique',
      desc: 'Au cœur de la médina, dans un derb calme et préservé'
    }
  ];

  return (
    <div data-testid="riad-page" className="bg-[#FAF8F5] min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/3zxllbtr_B6FC2C6B-A77D-4138-AE1D-B5225D71123A.JPG"
            alt="Vue du patio"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>
        </div>

        <div className="relative z-10 text-center px-8 max-w-6xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-[#D4AF37] text-sm uppercase tracking-[0.3em] mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {isEn ? 'Discover' : 'Découvrez'}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5 }}
            className="font-serif text-6xl md:text-8xl lg:text-9xl text-white mb-8 leading-[0.95]"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}
          >
            Riad KAMA
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.7 }}
            className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}
          >
            {isEn 
              ? 'An exceptional residence where the soul of Morocco meets contemporary refinement'
              : 'Une demeure d\'exception où l\'âme du Maroc rencontre le raffinement contemporain'}
          </motion.p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-32 md:py-48 px-8 md:px-16 lg:px-32">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-20">
            <div className="w-24 h-[2px] bg-[#D4AF37] mx-auto mb-12"></div>
            <h2 className="font-serif text-4xl md:text-6xl text-[#1A1A1A] mb-12" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}>
              {isEn ? 'A story of authenticity and refinement' : 'Une histoire d\'authenticité et de raffinement'}
            </h2>
            <div className="space-y-8 text-lg text-[#1A1A1A]/80 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}>
              <p>
                {isEn 
                  ? 'Riad KAMA is not simply a prestigious address in Marrakech. It is an invitation to experience the very essence of Moroccan hospitality in its noblest expression. Nestled at the end of a peaceful derb in the historic Kasbah district, this 400 m² riad is an ode to traditional Moroccan architecture, enhanced by a meticulous renovation that respects every ancestral detail while instilling resolutely modern comfort.'
                  : 'Riad KAMA n\'est pas simplement une adresse prestigieuse à Marrakech. C\'est une invitation à vivre l\'essence même de l\'hospitalité marocaine dans son expression la plus noble. Niché au fond d\'un derb paisible du quartier historique de la Kasbah, ce riad de 400 m² est une ode à l\'architecture traditionnelle marocaine, sublimée par une rénovation méticuleuse qui respecte chaque détail ancestral tout en y insufflant un confort résolument moderne.'}
              </p>
              <p>
                {isEn
                  ? 'Imagine crossing the massive carved wooden door and discovering a majestic central patio, a sanctuary of freshness and serenity. The soothing murmur of the traditional fountain mingles with the play of light dancing on walls adorned with meticulously selected zellige. Every arch, every column tells a story, that of a craftsmanship passed down from generation to generation.'
                  : 'Imaginez franchir la porte massive en bois sculpté et découvrir un patio central majestueux, sanctuaire de fraîcheur et de sérénité. Le murmure apaisant de la fontaine traditionnelle se mêle aux jeux de lumière qui dansent sur les murs ornés de zellige minutieusement sélectionné. Chaque arcade, chaque colonne raconte une histoire, celle d\'un savoir-faire transmis de génération en génération.'}
              </p>
              <p className="text-[#B8860B] text-xl font-medium">
                {isEn
                  ? 'But what truly distinguishes Riad KAMA is its philosophy: absolute exclusivity. Here, no rooms rented separately. The riad belongs entirely to you, whether you are two or ten. It is your private home in Marrakech, your personal sanctuary in the medina.'
                  : 'Mais ce qui distingue vraiment Riad KAMA, c\'est sa philosophie : l\'exclusivité absolue. Ici, pas de chambres louées séparément. Le riad vous appartient entièrement, que vous soyez deux ou dix. C\'est votre maison privée à Marrakech, votre sanctuaire personnel dans la médina.'}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Highlights Grid */}
      <section className="py-32 px-8 md:px-16 lg:px-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: index * 0.1 }}
                className="glass-dark p-10 border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-all duration-500 group"
              >
                <div className="text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform duration-500">
                  {item.icon}
                </div>
                <h3 className="font-serif text-3xl text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {item.title}
                </h3>
                <p className="text-[#D4AF37] text-sm uppercase tracking-wider mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {item.subtitle}
                </p>
                <p className="text-white/70 text-sm leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}>
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section className="py-32 md:py-48 px-8 md:px-16 lg:px-32 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div {...fadeInUp}>
              <div className="relative h-[700px] overflow-hidden border border-[#D4AF37]/30 shadow-lg">
                <img
                  src="/images/rey0acg7_616FD1E8-33A4-4E04-A26C-D74C9EE4E5BC.JPG"
                  alt="Salle à manger"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-[2s]"
                />
              </div>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.3 }} className="space-y-8">
              <div className="w-16 h-[2px] bg-[#D4AF37]"></div>
              <h2 className="font-serif text-4xl md:text-6xl text-[#1A1A1A]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}>
                {isEn ? 'Architecture at the service of emotion' : 'L\'architecture au service de l\'émotion'}
              </h2>
              <div className="space-y-6 text-base text-[#1A1A1A]/80 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}>
                <p>
                  {isEn
                    ? 'The central patio is the soul of the riad. Surrounded by elegant arcades and bathed in natural light, it creates a unique atmosphere where time seems suspended. The traditional fountain at its center brings that touch of freshness and serenity so characteristic of authentic Moroccan riads.'
                    : 'Le patio central est l\'âme du riad. Entouré d\'arcades élégantes et baigné de lumière naturelle, il crée une atmosphère unique où le temps semble suspendu. La fontaine traditionnelle en son centre apporte cette touche de fraîcheur et de sérénité si caractéristique des riads marocains authentiques.'}
                </p>
                <p>
                  {isEn
                    ? 'Two Moroccan lounges invite you to relax: one on the ground floor for convivial moments, the other on the rooftop for evenings under the stars. Each space has been designed to create unforgettable moments, whether for mint tea in the late afternoon or a candlelit dinner under the celestial vault.'
                    : 'Deux salons marocains vous invitent à la détente : l\'un au rez-de-chaussée pour des moments conviviaux, l\'autre sur le rooftop pour des soirées sous les étoiles. Chaque espace a été pensé pour créer des moments inoubliables, que ce soit pour un thé à la menthe en fin d\'après-midi ou un dîner aux chandelles sous la voûte céleste.'}
                </p>
                <p>
                  {isEn
                    ? 'The rooftop is your private kingdom. With its refreshing pool, its landscaped terrace and its solarium upper terrace, it offers breathtaking views over the rooftops of the medina and the snow-capped peaks of the Atlas in the distance. This is where you will have your breakfast in the sun, where you will lounge in the afternoon, where your most beautiful Moroccan memories will be created.'
                    : 'Le rooftop est votre royaume privé. Avec son bassin rafraîchissant, sa terrasse aménagée et sa sur-terrasse solarium, il offre des vues imprenables sur les toits de la médina et les sommets enneigés de l\'Atlas au loin. C\'est ici que vous prendrez vos petits-déjeuners au soleil, ici que vous vous prélasserez l\'après-midi, ici que se créeront vos plus beaux souvenirs marocains.'}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-32 px-8 md:px-16 lg:px-32 bg-[#0A0A0A]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-20">
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {isEn ? 'Gallery' : 'Galerie'}
            </p>
            <h2 className="font-serif text-4xl md:text-6xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}>
              {isEn ? 'Every space tells a story' : 'Chaque espace raconte une histoire'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              '/images/vwso2uf5_3991822D-CFC2-4845-9DBB-8175E45A614D.JPG',
              '/images/2s55i11u_2ABB33CC-B1BB-4678-A614-5B16E824815A.JPG',
              '/images/yqfeajfv_E9B3F343-832B-4FD3-AC15-814AE9955BAF.JPG',
              '/images/17wjim7n_17CCA6B1-4307-4C0A-B62A-6C7BDC845220.JPG',
              '/images/i7m3l513_8693075F-13C8-4725-A3D8-3C6B4C460578.JPG',
              '/images/8028fiov_4A6A62E0-7C16-4E1E-8193-A51E85F20450.JPG'
            ].map((img, index) => (
              <motion.div
                key={index}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: index * 0.1 }}
                className="relative overflow-hidden group h-96 border border-[#D4AF37]/20"
              >
                <img
                  src={img}
                  alt={`Riad KAMA ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1.5s]"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-48 px-8 md:px-16 lg:px-32 text-center bg-[#FAF8F5]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp}>
            <p className="text-[#B8860B] text-xs uppercase tracking-[0.3em] mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {isEn ? 'Book your stay' : 'Réservez votre séjour'}
            </p>
            <h2 className="font-serif text-5xl md:text-7xl mb-12 text-[#1A1A1A]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}>
              {isEn ? 'Experience Riad KAMA' : 'Vivez l\'expérience Riad KAMA'}
            </h2>
            <button
              onClick={() => navigate('/disponibilites')}
              className="gold-gradient text-[#0A0A0A] px-16 py-6 hover:shadow-2xl transition-all duration-700 uppercase tracking-[0.2em] text-xs font-medium inline-flex items-center gap-3 group"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {isEn ? 'Check availability' : 'Consulter les disponibilités'}
              <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform duration-500" />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TheRiad;
